import { openPresets } from '@/components/common/pill/preset'

import type {
	CollectorStatus,
	DiagnosticsCheckStatus,
	LogSourceStatus,
	VerifyRecordVerdict,
} from './types'
import type { VerifyCheckStatus } from './verify-diagnostics'
import type { VerifyState } from './verify-outcome'
import type { PillPreset } from '@/components/common/pill/preset'

/**
 * The one owner of **Collector** status labels and colours (ADR 0020 §5).
 *
 * `connected` reads *Connected* rather than *Healthy* deliberately: an operator
 * connects an agent to AIR®, and the word has to match what the Add Collector
 * flow says when the heartbeat first arrives.
 */
const COLLECTOR_STATUS_PRESET_RECORD: Record<CollectorStatus, PillPreset> = {
	connected: { label: 'Connected', tone: 'success' },
	degraded: { label: 'Degraded', tone: 'alert' },
	offline: { label: 'Offline', tone: 'error' },
}

/**
 * Opened for lookup for the same reason every other domain's table is: the
 * status will arrive as a string over the wire, and a value the frontend has
 * never heard of must paint a grey pill rather than nothing (P-17).
 */
export const COLLECTOR_STATUS_PRESETS = openPresets(
	'collector status',
	COLLECTOR_STATUS_PRESET_RECORD,
)

/**
 * The one owner of **Log Source** status labels and colours — a different axis
 * from the Collector's, and kept as a second record on purpose (conflating the
 * two is the trap this pairing sets).
 *
 * `awaiting_first_logs` is `pending`, not `alert`: a source that has never
 * received a log is new, not broken, and colouring it amber would send an
 * operator looking for a fault that doesn't exist.
 */
const LOG_SOURCE_STATUS_PRESET_RECORD: Record<LogSourceStatus, PillPreset> = {
	receiving: { label: 'Receiving', tone: 'success' },
	silent: { label: 'Silent', tone: 'alert' },
	awaiting_first_logs: { label: 'Awaiting first logs', tone: 'pending' },
}

/** Opened for lookup — see {@link COLLECTOR_STATUS_PRESETS}. */
export const LOG_SOURCE_STATUS_PRESETS = openPresets(
	'log source status',
	LOG_SOURCE_STATUS_PRESET_RECORD,
)

/**
 * The one owner of **diagnostics check** labels and tones.
 *
 * A third vocabulary rather than a reuse of either status enum: a check reports
 * on an *observation*, not on a Collector or a Log Source, and the two words it
 * needs (`Pass` / `Warning`) say nothing about liveness or data flow. `warn` is
 * `alert`, never `error` — none of these checks can prove a device is broken,
 * only that the collector could not confirm it works.
 */
const DIAGNOSTICS_CHECK_PRESET_RECORD: Record<
	DiagnosticsCheckStatus,
	PillPreset
> = {
	pass: { label: 'Pass', tone: 'success' },
	warn: { label: 'Warning', tone: 'alert' },
}

/** Opened for lookup — see {@link COLLECTOR_STATUS_PRESETS}. */
export const DIAGNOSTICS_CHECK_PRESETS = openPresets(
	'diagnostics check',
	DIAGNOSTICS_CHECK_PRESET_RECORD,
)

/**
 * The one owner of the **Verify step's** state labels and tones.
 *
 * An exhaustive `Record` used directly, **not** run through `openPresets`: this
 * status never comes off a wire. It is derived on the client from a Collector's
 * liveness (`verifyOutcomeFor`), so there is no `VARCHAR` that could hand us a
 * value the record has never heard of — and the fallback that would buy is a
 * grey pill in place of a compile error (ADR 0020 §5).
 *
 * `listening` and `no-records` are both `pending` rather than escalating to
 * `alert`: nothing has gone wrong in either, and a listener waiting for a device
 * somebody is still configuring is the normal opening minute of this flow. Only
 * `egress-error` is `error`, because that one is a fault on our side of the
 * wire.
 */
export const VERIFY_STATE_PRESETS: Record<VerifyState, PillPreset> = {
	'listening': { label: 'No records yet', tone: 'pending' },
	'live': { label: 'Shipping to AIR®', tone: 'success' },
	'no-records': { label: 'No records for 5 min', tone: 'pending' },
	'egress-error': { label: 'Not shipping to AIR®', tone: 'error' },
}

/**
 * The one owner of a parsed record's **verdict** chip.
 *
 * Upper-case labels, against the house's sentence case, because these are not
 * our words: `ALLOW` / `DENY` is what the device wrote in the raw line directly
 * above the chip, and sentence-casing it would make the parsed row look like an
 * interpretation rather than a reading.
 *
 * `alert` is amber and `deny` is red, which is the device's severity and not
 * AIR®'s: a denied session is a firewall doing its job, so nothing here is a
 * finding — it is evidence that parsing works.
 */
export const VERIFY_RECORD_VERDICT_PRESETS: Record<
	VerifyRecordVerdict,
	PillPreset
> = {
	allow: { label: 'ALLOW', tone: 'neutral' },
	alert: { label: 'ALERT', tone: 'alert' },
	deny: { label: 'DENY', tone: 'error' },
}

/**
 * The one owner of the Verify step's **check** labels and tones.
 *
 * A fourth table rather than a reuse of `DIAGNOSTICS_CHECK_PRESETS`, because two
 * of its four members are states of the *run* rather than verdicts — a check
 * that has not been asked anything yet cannot be a `Pass` or a `Warning` (see
 * `VerifyCheckStatus`). The two verdicts it shares are spelled the same way as
 * the manage page's on purpose.
 */
export const VERIFY_CHECK_PRESETS: Record<VerifyCheckStatus, PillPreset> = {
	ready: { label: 'Ready', tone: 'neutral' },
	running: { label: 'Running…', tone: 'pending' },
	pass: { label: 'Pass', tone: 'success' },
	warn: { label: 'Warning', tone: 'alert' },
}
